import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import ConnectToolModal, { PickToolModal, toolOfKind } from './ConnectToolModal';
import ToolIcon from './ToolIcon';
import { SkeletonBar } from '../Skeletons';
import { formatRelativeTime } from '../../utils/relativeTime';
import './sandbox.css';

/**
 * Features › Connections › Your AI tools: the AI tools (MCP clients) that reached the person's sandbox, from the
 * overview's `aiTools` (`[{ kind, label, lastSeenAt }]`, the same list as Settings › How you build › Your AI tool),
 * each with when it was last used and its setup. "Connect another" opens the tool picker of first-run step 2.
 */
export default function YourAiTools({ sandbox = null, loading = false }) {
  const [picking, setPicking] = useState(false);
  const [setupOf, setSetupOf] = useState(null);
  const seen = sandbox?.aiTools || [];
  // Until the sandbox answers, which tools connected is not known: a placeholder, not "none yet".
  const waiting = loading && !sandbox;

  return (
    <section aria-labelledby="your-ai-tools-title" className="sbx-card connections-tools" id="your-ai-tools">
      <div className="connections-tools-head">
        <div className="connections-tools-text">
          <h2 id="your-ai-tools-title">Your AI tools</h2>
          <p>
            The AI tools that work on your sandbox through MCP: Claude, Cursor, ChatGPT, GitHub Copilot and others. Each one
            gets your projects, a shell and live previews with one connection.
          </p>
        </div>
        {!waiting && (
          <Button onClick={() => setPicking(true)} type={seen.length ? 'default' : 'primary'}>
            {seen.length ? 'Connect another' : 'Connect your AI tool'}
          </Button>
        )}
      </div>

      {waiting && <span aria-busy="true" aria-label="Loading your AI tools"><SkeletonBar height={12} width="45%" /></span>}
      {!waiting && seen.length === 0 && (
        <p className="connections-tools-empty">
          No AI tool has connected to your sandbox yet. Pick yours, follow its setup, and it shows up here after its first call.
        </p>
      )}
      {!waiting && seen.length > 0 && (
        <ul aria-label="Connected AI tools" className="connections-tools-list">
          {seen.map((entry) => {
            const tool = toolOfKind(entry.kind);
            const name = entry.label || tool.name;
            return (
              <li className="connections-tool" key={`${entry.kind}-${entry.label || ''}`}>
                <ToolIcon icon={tool.icon} label={name} size={20} />
                <span className="connections-tool-text">
                  <strong>{name}</strong>
                  <span>{entry.lastSeenAt ? `Last used ${formatRelativeTime(entry.lastSeenAt)}` : 'Connected'}</span>
                </span>
                <Button aria-label={`Setup of ${name}`} onClick={() => setSetupOf(tool)} size="small" type="link">Setup</Button>
              </li>
            );
          })}
        </ul>
      )}

      <PickToolModal onClose={() => setPicking(false)} open={picking} sandbox={sandbox} />
      <ConnectToolModal onClose={() => setSetupOf(null)} tool={setupOf} />
    </section>
  );
}

YourAiTools.propTypes = {
  sandbox: PropTypes.object,
  loading: PropTypes.bool,
};
